// frontend/shared-ui/src/forms/schemas/cable.js

const FIBER_STANDARDS = [
    { value: 'G.652.D', label: 'G.652.D — Standard single-mode' },
    { value: 'G.655', label: 'G.655 — NZ-DSF (for DWDM)' },
    { value: 'G.657.A1', label: 'G.657.A1 — Bend-insensitive (10mm)' },
    { value: 'G.657.A2', label: 'G.657.A2 — Bend-insensitive (7.5mm)' },
    { value: 'G.657.B3', label: 'G.657.B3 — Bend-insensitive (5mm)' },
    { value: 'OM1', label: 'OM1 — 62.5µm multimode' },
    { value: 'OM2', label: 'OM2 — 50µm multimode' },
    { value: 'OM3', label: 'OM3 — 50µm laser-optimized' },
    { value: 'OM4', label: 'OM4 — 50µm extended reach' },
    { value: 'OM5', label: 'OM5 — Wideband multimode' },
];

const CONNECTOR_OPTIONS = ['SC', 'LC', 'FC', 'ST', 'E2000', 'MPO', 'MTP'];
const POLISH_OPTIONS = [
    { value: 'APC', label: 'APC (Green)' },
    { value: 'UPC', label: 'UPC (Blue)' },
    { value: 'PC', label: 'PC' },
];

const MODE_CODES = {
    Singlemode: 'SM',
    'Multimode OM1': 'OM1',
    'Multimode OM2': 'OM2',
    'Multimode OM3': 'OM3',
    'Multimode OM4': 'OM4',
    'Multimode OM5': 'OM5',
};

function endLabel(type, polish) {
    if (!type) return '';
    return polish ? `${type}/${polish}` : type;
}

export default {
    slug: 'cable',
    label: 'Fiber Cable',
    defaultUnit: 'm',
    costDefaults: {
        avgVolumeCbm: 0.05,
        suggestedMarkupPct: 30,
        freightSensitivity: 'high',
        note: 'Cables ship on spools — CBM varies by length and core count',
    },

    groups: [
        {
            id: 'basic',
            label: 'Basic',
            columns: 2,
            fields: [
                {
                    key: 'cableType',
                    label: 'Cable Type',
                    type: 'select',
                    required: true,
                    options: ['Drop', 'ADSS', 'Aerial', 'Duct', 'Direct Burial', 'Indoor', 'Distribution', 'Ribbon', 'Bunched'],
                },
                {
                    key: 'coreCount',
                    label: 'Core Count',
                    type: 'number',
                    required: true,
                    unit: 'cores',
                    min: 1,
                },
            ],
        },
        {
            id: 'optical',
            label: 'Optical',
            columns: 2,
            fields: [
                {
                    key: 'fiberMode',
                    label: 'Fiber Mode',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'Singlemode', label: 'Singlemode (SM)' },
                        { value: 'Multimode OM1', label: 'Multimode OM1' },
                        { value: 'Multimode OM2', label: 'Multimode OM2' },
                        { value: 'Multimode OM3', label: 'Multimode OM3' },
                        { value: 'Multimode OM4', label: 'Multimode OM4' },
                        { value: 'Multimode OM5', label: 'Multimode OM5' },
                    ],
                },
                {
                    key: 'fiberStandard',
                    label: 'Fiber Standard (ITU-T / TIA)',
                    type: 'select',
                    required: true,
                    options: FIBER_STANDARDS,
                    hint: 'e.g. G.657.A1 for FTTH bend-insensitive fiber',
                },
                {
                    key: 'fiberManufacturer',
                    label: 'Fiber Manufacturer / Product',
                    type: 'text',
                    placeholder: 'e.g. Corning ClearCurve ZBL',
                    hint: 'Optional — the raw glass supplier if known',
                },
            ],
        },
        // NEW: Side A & Side B for connector ends
        {
            id: 'connectors',
            label: 'Connectors (Both Ends)',
            columns: 2,
            fields: [
                {
                    key: 'connectorTypeA',
                    label: 'Connector (Side A)',
                    type: 'select',
                    options: CONNECTOR_OPTIONS,
                },
                {
                    key: 'polishTypeA',
                    label: 'Polish (Side A)',
                    type: 'select',
                    options: POLISH_OPTIONS,
                },
                {
                    key: 'connectorTypeB',
                    label: 'Connector (Side B)',
                    type: 'select',
                    options: CONNECTOR_OPTIONS,
                },
                {
                    key: 'polishTypeB',
                    label: 'Polish (Side B)',
                    type: 'select',
                    options: POLISH_OPTIONS,
                },
            ],
        },
        {
            id: 'mechanical',
            label: 'Mechanical',
            columns: 3,
            fields: [
                { key: 'jacket', label: 'Jacket Material', type: 'select', options: ['LSZH', 'PVC', 'PE', 'HDPE', 'Plenum', 'OFNR'] },
                { key: 'diameter_mm', label: 'Outer Diameter', type: 'number', unit: 'mm', step: 0.1 },
                { key: 'weight_kgkm', label: 'Weight', type: 'number', unit: 'kg/km' },
                { key: 'tensileStrength_n', label: 'Tensile Strength', type: 'number', unit: 'N' },
                { key: 'bendRadius_mm', label: 'Min Bend Radius', type: 'number', unit: 'mm' },
                { key: 'tempRange', label: 'Temp Range', type: 'text', placeholder: '-40 to +70 °C' },
            ],
        },
        {
            id: 'packaging',
            label: 'Packaging',
            columns: 3,
            fields: [
                { key: 'lengthMeters', label: 'Length per unit', type: 'number', unit: 'm', min: 1 },
                { key: 'packageType', label: 'Package Type', type: 'select', options: ['Drum', 'Roll', 'Coil', 'Box'] },
                { key: 'color', label: 'Jacket Color', type: 'select', options: ['Black', 'Orange', 'Yellow', 'Aqua', 'Blue', 'Green', 'Red'] },
            ],
        },
    ],

    buildSku(v) {
        const sideA = [v.connectorTypeA, v.polishTypeA].filter(Boolean).join('-');
        const sideB = [v.connectorTypeB, v.polishTypeB].filter(Boolean).join('-');

        const parts = [
            'CBL',
            v.cableType ? v.cableType.toUpperCase().replace(/\s+/g, '') : '',
            (v.coreCount || '').toString().padStart(3, '0'),
            MODE_CODES[v.fiberMode] || '',
            v.fiberStandard ? v.fiberStandard.replace(/[.]/g, '') : '',
            sideA,
            sideB,
            v.jacket || '',
            v.lengthMeters ? `${v.lengthMeters}M` : '',
        ];
        return parts.filter(Boolean).join('-').toUpperCase().replace(/\s+/g, '');
    },

    buildName(v) {
        const sideALabel = endLabel(v.connectorTypeA, v.polishTypeA);
        const sideBLabel = endLabel(v.connectorTypeB, v.polishTypeB);

        const bits = [];

        // End labels: "SC/UPC to SC/APC"
        if (sideALabel && sideBLabel) {
            bits.push(`${sideALabel} to ${sideBLabel}`);
        } else if (sideALabel) {
            bits.push(sideALabel);
        }

        if (v.coreCount) bits.push(`${v.coreCount}-Core`);
        if (v.fiberMode) bits.push(v.fiberMode);
        if (v.fiberStandard) bits.push(v.fiberStandard);
        if (v.jacket) bits.push(v.jacket);
        if (v.cableType) bits.push(`${v.cableType} Cable`);
        if (v.lengthMeters) bits.push(`(${v.lengthMeters}m)`);

        return bits.join(' ').trim();
    },

    attributeSchema: [
        { key: 'cableType', label: 'Cable Type' },
        { key: 'coreCount', label: 'Core Count' },
        { key: 'fiberMode', label: 'Fiber Mode' },
        { key: 'fiberStandard', label: 'Fiber Standard' },
        { key: 'fiberManufacturer', label: 'Fiber Manufacturer' },
        { key: 'connectorTypeA', label: 'Connector (Side A)' },
        { key: 'polishTypeA', label: 'Polish (Side A)' },
        { key: 'connectorTypeB', label: 'Connector (Side B)' },
        { key: 'polishTypeB', label: 'Polish (Side B)' },
        { key: 'jacket', label: 'Jacket' },
        { key: 'diameter_mm', label: 'Outer Diameter', unit: 'mm' },
        { key: 'weight_kgkm', label: 'Weight', unit: 'kg/km' },
        { key: 'tensileStrength_n', label: 'Tensile Strength', unit: 'N' },
        { key: 'bendRadius_mm', label: 'Min Bend Radius', unit: 'mm' },
        { key: 'tempRange', label: 'Operating Temp' },
        { key: 'lengthMeters', label: 'Length', unit: 'm' },
        { key: 'packageType', label: 'Package Type' },
        { key: 'color', label: 'Jacket Color' },
    ],
};